/** Shared helpers for CapIQ-shaped comps rows in 409admin. */

/** Sticky row 0 = subject (portfolio) company — not a fake "HEADER" label. */
export const EMPTY_SUBJECT_COMPANY = {
  Identifier: '',
  Name: '',
  Active: 1,
  Multiples: 0,
  Outlier: 0,
  'Subject Company': 1,
  'Short Description': '',
  'Long Description': '',
  'IPO Date': '',
};

/** @deprecated use EMPTY_SUBJECT_COMPANY — kept so older imports don't break */
export const HEADER_ROW = EMPTY_SUBJECT_COMPANY;

export function oldToNewFormat(oldFormat) {
  if (!oldFormat) return [];
  if (Array.isArray(oldFormat)) return oldFormat;
  const newFormat = [];
  Object.keys(oldFormat).forEach((key) => {
    const values = oldFormat[key] || [];
    values.forEach((val, index) => {
      if (!newFormat[index]) newFormat[index] = {};
      newFormat[index][key] = val;
    });
  });
  return newFormat;
}

function rawIdentifier(comp) {
  const id = comp?.Identifier;
  if (Array.isArray(id)) return id[0];
  return id;
}

/** Stable CapIQ id string for keys / delete compares. */
export function compIdentifier(compOrId) {
  if (compOrId == null) return '';
  if (typeof compOrId === 'string' || typeof compOrId === 'number') {
    return String(compOrId).trim();
  }
  return String(rawIdentifier(compOrId) ?? '').trim();
}

/**
 * CapIQ / S3 / Decimal JSON can leave flags as "1", "1.0", [1], true, etc.
 * Treat only clearly-on values as on; everything else as off.
 */
export function isFlagOn(val) {
  if (val === 1 || val === true || val === '1') return true;
  if (typeof val === 'number' && val === 1) return true;
  if (typeof val === 'string' && (val === '1' || val === '1.0' || val.toLowerCase() === 'true')) {
    return true;
  }
  if (Array.isArray(val)) return isFlagOn(val[0]);
  return false;
}

export function flagToBit(val) {
  return isFlagOn(val) ? 1 : 0;
}

export function toggleFlag(val) {
  return isFlagOn(val) ? 0 : 1;
}

/** Coerce Active / Multiples / Outlier to 0|1 ints so checkbox / flag / sort work. */
export function normalizeCompFlags(comp) {
  if (!comp || typeof comp !== 'object') return comp;
  return {
    ...comp,
    Identifier: compIdentifier(comp) || (Array.isArray(comp.Identifier) ? '' : (comp.Identifier ?? '')),
    Active: flagToBit(comp.Active ?? 1),
    Multiples: flagToBit(comp.Multiples ?? 0),
    Outlier: flagToBit(comp.Outlier ?? 0),
    'Market Outlier': flagToBit(comp['Market Outlier'] ?? 0),
    'Subject Company': flagToBit(comp['Subject Company'] ?? 0),
  };
}

function isHeaderSpacer(comp) {
  const id = String(rawIdentifier(comp) ?? '').trim();
  const name = String(comp?.Name ?? '').trim();
  return id === 'HEADER' || name === 'HEADER';
}

function isSubjectRow(comp) {
  return (
    comp?.['Subject Company'] === 1 ||
    comp?.['Subject Company'] === '1' ||
    comp?.['Subject Company'] === true
  );
}

/** Drop spacer / junk rows; keep real CapIQ comps + at most one subject. */
export function removeDuplicateComps(comps) {
  const uniqueIds = [];
  return (comps || []).filter((comp) => {
    if (!comp || isHeaderSpacer(comp)) return false;
    const id = rawIdentifier(comp);
    if (id == null || id === '') {
      // empty id only OK for subject company
      if (!isSubjectRow(comp)) return false;
      if (uniqueIds.includes('__SUBJECT__')) return false;
      uniqueIds.push('__SUBJECT__');
      return true;
    }
    if (uniqueIds.includes(id)) return false;
    uniqueIds.push(id);
    return true;
  });
}

export function ensureSubjectCompanyFirst(comps) {
  let list = removeDuplicateComps(oldToNewFormat(comps)).map(normalizeCompFlags);

  const subjectIndex = list.findIndex(isSubjectRow);
  let subject;
  let rest;
  if (subjectIndex >= 0) {
    const raw = list[subjectIndex];
    subject = normalizeCompFlags({
      ...EMPTY_SUBJECT_COMPANY,
      ...raw,
      Identifier: '', // never show "HEADER" / junk in CapIQ column for sticky row
      Name: raw.Name && raw.Name !== 'HEADER' ? raw.Name : (raw.Name || ''),
      'Subject Company': 1,
      Multiples: 0,
    });
  } else {
    subject = { ...EMPTY_SUBJECT_COMPANY };
  }
  rest = (subjectIndex >= 0 ? list.filter((_, i) => i !== subjectIndex) : list)
    .filter((c) => {
      const id = compIdentifier(c);
      return id && id !== 'HEADER';
    })
    .map((c) => normalizeCompFlags({ ...c, 'Subject Company': 0 }));
  return [subject, ...rest];
}

/** Ensure index 0 is the subject company (820 sticky row). Drop fake HEADER rows. */
export function ensureHeaderRow(comps) {
  return ensureSubjectCompanyFirst(comps);
}

/** Rows safe to POST to summarize / outliers (no spacer, no subject — BE re-inserts subject). */
export function compsForApi(comps) {
  return ensureSubjectCompanyFirst(comps)
    .slice(1)
    .filter((c) => {
      const id = String(rawIdentifier(c) ?? '').trim();
      return id && id !== 'HEADER';
    });
}

export function buildDefaultCalcData() {
  return {
    limits: {
      volatility: { quantile: '50%' },
      term: { selectedTerm: '3 years', calculateRange: 1, upperBuffer: 1, lowerBuffer: 1, increment: 0.5 },
    },
    methods: {
      backsolve: {
        weight: 100,
        marketAdjustment: '0%',
        manualTransactionDateVolatility: null,
        manualTransactionDateTerm: null,
        suggestedMarketAdjustment: null,
      },
      pubCo: {
        weight: 0,
        enterpriseValue: '',
        equityValue: '',
        subMethods: [],
      },
      mna: {
        weight: 0,
        enterpriseValue: '',
        equityValue: '',
      },
    },
  };
}

export function buildDefaultFootnoteData() {
  return {
    volPercentFn: '',
    tdVolatilityBacksolve: '',
    methodsFootnotes: [],
  };
}

/**
 * Map 409A company/transaction into the shape Comps + EquityInputs expect.
 * Note: TabsContent often receives `companyToViewData.transactionData` as the
 * `companyToViewData` prop (i.e. the txn object itself, not the wrapper).
 */
/** Wrap a scalar KPI the way ASC 820 enterpriseFinancialInfo expects. */
function wrapFinancialValue(val) {
  if (val === null || val === undefined || val === '') return null;
  const cleaned = String(val).replace(/,/g, '').trim();
  if (!cleaned || Number.isNaN(parseFloat(cleaned))) return null;
  return { value: cleaned };
}

/**
 * 409A stores CFY/NFY/LTM/NTM revenue & EBITDA on the txn root; EquityInputs
 * reads enterpriseFinancialInfo.{currentFiscalYearRevenue|last12MonthsRevenue|...}.value
 * — same keys as ASC 820 Input Form → Comps Calculate equity.
 */
function buildEnterpriseFinancialInfo(txn = {}) {
  const existing = txn.enterpriseFinancialInfo && typeof txn.enterpriseFinancialInfo === 'object'
    ? { ...txn.enterpriseFinancialInfo }
    : {};

  const mapped = {
    // CFY / NFY
    currentFiscalYearRevenue: wrapFinancialValue(txn.totalCurrentFiscalYearRevenue),
    nextFiscalYearRevenue: wrapFinancialValue(txn.totalNextFiscalYearRevenue),
    currentFiscalYearEBITDA: wrapFinancialValue(txn.totalCurrentFiscalYearEBITDA),
    nextFiscalYearEBITDA: wrapFinancialValue(txn.totalNextFiscalYearEBITDA),
    // LTM / NTM (client + admin ClientInputs)
    last12MonthsRevenue: wrapFinancialValue(txn.ltmRevenue),
    next12MonthsRevenue: wrapFinancialValue(txn.ntmRevenue),
    last12MonthsEBITDA: wrapFinancialValue(txn.ltmEBITDA),
    next12MonthsEBITDA: wrapFinancialValue(txn.ntmEBITDA),
  };

  Object.entries(mapped).forEach(([key, wrapped]) => {
    if (!wrapped) return;
    const prev = existing[key];
    const prevVal = prev && typeof prev === 'object' ? prev.value : prev;
    if (prevVal === null || prevVal === undefined || prevVal === '') {
      existing[key] = { ...(typeof prev === 'object' && prev ? prev : {}), ...wrapped };
    }
  });

  return existing;
}

export function buildCompsUserData(companyToViewData = {}) {
  const txn = companyToViewData?.transactionData && companyToViewData.transactionData.transactionId
    ? companyToViewData.transactionData
    : (companyToViewData || {});
  const companyId = txn.companyId
    || companyToViewData?.companyData?.companyId
    || companyToViewData?.companyId
    || '';
  const transactionId = txn.transactionId
    || companyToViewData?.transactionId
    || '';
  const industry = txn.industryVertical
    ? [txn.industryVertical]
    : (txn.basicInfo?.companyIndustry || ['']);

  const companyName = companyToViewData?.companyData?.companyName
    || companyToViewData?.accountData?.companyName
    || txn.companyName
    || '';

  const enterpriseFinancialInfo = buildEnterpriseFinancialInfo(txn);

  return {
    metaData: {
      portfolioCompanyId: companyId,
      companyId,
      transactionId,
      companyName,
    },
    transactionData: {
      ...txn,
      companyId,
      portfolioCompanyId: companyId,
      enterpriseCompanyId: companyId,
      transactionId,
      companyName,
      basicInfo: {
        valuationDate: txn.valuationDate || txn.transactionDate || '',
        transactionDate: txn.transactionDate || '',
        financialStatementDate: txn.financialStatementDate || txn.valuationDate || txn.transactionDate || '',
        companyIndustry: industry,
      },
      enterpriseFinancialInfo,
      clientFinancialInfo: txn.clientFinancialInfo || {
        currentCash: { value: String(txn.totalCurrentCash || '0') },
        convertibleDebt: { value: String(txn.totalConvertibleDebt || '0') },
        nonConvertibleDebt: { value: String(txn.totalNonConvertibleDebt || '0') },
        totalDebt: { value: '0' },
      },
      compStatus: txn.compStatus,
      compsLocation: txn.compsLocation,
      compSummaryLocation: txn.compSummaryLocation,
    },
    calcData: buildDefaultCalcData(),
    footnoteData: buildDefaultFootnoteData(),
    gridData: {
      defaultScenarios: { term: 3, volatility: 40 },
    },
  };
}
